import config from "../../config";
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { SunIcon, MoonIcon, XMarkIcon, Bars3Icon } from "@heroicons/react/24/outline";

const menuItems = [
  { name: "Home", path: "/", key: "home" },
  { name: "Transfer", path: "/transfer-playlists-and-songs", key: "transfer" },
  { name: "Sync", path: "/sync-playlists", key: "sync" },
  { name: "Blog", path: "/blog", key: "blog" },
  { name: "Contact", path: "/contact-us", key: "contact" },
];

export default function HeaderComponent() {
  const location = useLocation();
  const path = location.pathname;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const selected = (() => {
    if (path === "/") return "home";
    if (path.includes("/transfer")) return "transfer";
    if (path.includes("/sync")) return "sync";
    if (path.includes("/blog")) return "blog";
    if (path.includes("/contact")) return "contact";
    if (path.includes("/login")) return "login";
    return "";
  })();

  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.theme === "dark" ||
        (!("theme" in localStorage) && window.matchMedia("(prefers-color-scheme: dark)").matches)
        ? "dark"
        : "light";
    }
    return "light";
  });

  useEffect(() => {
    const root = document.documentElement;

    root.classList.add("disable-transitions");

    if (theme === "dark") {
      root.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      root.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }

    setTimeout(() => {
      root.classList.remove("disable-transitions");
    }, 0);
  }, [theme]);

  function getClassNamesForLinks(linkName: string) {
    if (selected === linkName) {
      return "block py-2 pl-3 pr-4 text-white bg-purple-700 rounded lg:bg-transparent lg:text-purple-700 lg:p-0 dark:text-white"
    }
    return "block py-2 pl-3 pr-4 text-gray-700 border-b border-gray-100 hover:bg-gray-200 hover:text-gray-900 lg:hover:bg-transparent lg:border-0 lg:hover:text-purple-700 lg:p-0 dark:text-gray-400 lg:dark:hover:text-white dark:hover:bg-gray-700 dark:hover:text-white lg:dark:hover:bg-transparent dark:border-gray-700"
  }

  return (
    <header className="fixed w-full z-50">
      <nav className="bg-white border-gray-200 py-2.5 dark:bg-zinc-950">
        <div className="flex flex-wrap items-center justify-between max-w-screen-xl px-4 mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={require("../../assets/logo.png")}
              className="h-6 mr-3 sm:h-9"
              alt="Music Porter"
            />
          </Link>

          {/* Right Section (Theme, CTA, Mobile Button) */}
          <div className="flex items-center lg:order-2">
            {/* Theme Toggle */}
            <button
              onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
              className="p-1 sm:p-2 mr-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label="Toggle Theme"
            >
              {theme === "dark" ? (
                <SunIcon className="h-5 w-5 text-yellow-400" />
              ) : (
                <MoonIcon className="h-5 w-5 text-gray-700 dark:text-white" />
              )}
            </button>

            {/* CTA */}
            <Link
              to={config.signupPath}
              className="inline-flex items-center justify-center w-full sm:w-auto px-3 py-2 sm:px-5 sm:py-3 text-xs sm:text-sm font-medium text-center text-gray-900 border border-gray-200 rounded-md hover:bg-gray-200 focus:ring-4 focus:ring-gray-100 dark:text-white dark:border-gray-700 dark:hover:bg-gray-700 dark:focus:ring-gray-800"
            >
              Start Transfer
            </Link>

            {/* Hamburger Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md lg:hidden text-gray-900 hover:bg-gray-200 dark:text-white dark:hover:bg-gray-700 ml-2"
              aria-label="Toggle menu"
            >
              <span className="sr-only">Toggle menu</span>
              {mobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6 text-current" aria-hidden="true" />
              ) : (
                <Bars3Icon className="h-6 w-6 text-current" aria-hidden="true" />
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          <div
            id="mobile-menu-2"
            className={`w-full lg:hidden mt-2 bg-white dark:bg-zinc-950 border border-gray-200 dark:border-zinc-800 shadow-md rounded-md z-40 ${mobileMenuOpen ? "block" : "hidden"
              }`}
          >
            <ul className="flex flex-col mt-4 font-medium space-y-2 px-4 pb-4">
              {menuItems.map(({ name, path, key }) => (
                <li key={key}>
                  <Link
                    to={path}
                    className={getClassNamesForLinks(key)}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block py-2 pl-3 pr-4 text-gray-700 border-b border-gray-100 hover:bg-gray-200 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white dark:border-gray-700"
                >
                  Login
                </Link>
              </li>
            </ul>
          </div>

          {/* Desktop Menu */}
          <div className="items-center justify-between hidden w-full lg:flex lg:w-auto lg:order-1">
            <ul className="flex items-center flex-col mt-4 font-medium lg:flex-row lg:space-x-8 lg:mt-0">
              {menuItems.map(({ name, path, key }) => (
                <li key={key}>
                  <Link to={path} className={getClassNamesForLinks(key)}>
                    {name}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/login"
                  className={getClassNamesForLinks('login')}
                // className="block py-2 pl-3 pr-4 text-gray-700 hover:text-purple-700 dark:text-gray-400 dark:hover:text-white"
                >
                  Login
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
}
